import React, { memo } from "react";
import styles from "./BaseTableV2.module.scss";
import classNames from "classnames/bind";
import { HeadCelType } from "~/utils/types/common";
import { NextWhiteIcon } from "~/assets/icons";

type BaseTableV2Props<T extends { id: string }> = {
  data: Array<T>;
  renderCell: (item: T, index: number) => React.ReactElement;
  headCells: Array<HeadCelType>;
  page: number;
  totalPages: number;
  onChangePage?: (page: number) => void;
  onRowClick?: (item: T) => void;
  showPagination?: boolean;
  totalItems?: number;
};

const cx = classNames.bind(styles);

const BaseTableV2 = <T extends { id: string }>(props: BaseTableV2Props<T>) => {
  const {
    data,
    renderCell,
    headCells,
    onChangePage,
    totalPages,
    page,
    onRowClick,
    showPagination,
    totalItems,
  } = props;

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onChangePage) {
      onChangePage(newPage);
    }
  };

  const handleRowClick = (item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  return (
    <div className={cx("container")}>
      <div className={cx("table-wrapper", "table-wrapper-custom")}>
        <table>
          <thead>
            <tr>
              {headCells.map((headCell, index) => (
                <th
                  key={headCell.id as string}
                  align={"left"}
                  className={cx(`!w-[${headCell.width}]`)}
                >
                  <div className={cx("table-head-cell")}>
                    {headCell.label ?? headCell.id}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item: T, index: number) => {
              return (
                <tr
                  key={item.id}
                  className={cx(onRowClick ? "clickable" : "")}
                  onClick={() => handleRowClick(item)}
                >
                  {renderCell(item, index)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className={cx("pagination")}>
          <p className={cx("pagination-text")}>
            Showing {data.length} of {totalItems}
          </p>
          <div className={cx("pagination-buttons")}>
            <button
              className={cx("pagination-button", "previous")}
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1 || page === 0}
            >
              <NextWhiteIcon className={cx("previous-icon", " rotate-180 ")} />
            </button>
            <div className={cx("pagination-info")}>
              {(() => {
                const maxButtons = 5;
                const pages: (number | string)[] = [];
                if (totalPages <= maxButtons) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (page <= 3) {
                    pages.push(1, 2, 3, 4, "...", totalPages);
                  } else if (page >= totalPages - 2) {
                    pages.push(
                      1,
                      "...",
                      totalPages - 3,
                      totalPages - 2,
                      totalPages - 1,
                      totalPages
                    );
                  } else {
                    pages.push(
                      1,
                      "...",
                      page - 1,
                      page,
                      page + 1,
                      "...",
                      totalPages
                    );
                  }
                }
                const filteredPages = pages.filter(
                  (p, idx, arr) => !(p === arr[idx - 1])
                );
                return filteredPages.map((p, idx) =>
                  p === "..." ? (
                    <span key={idx} className={cx("pagination-ellipsis")}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={cx("pagination-page", { active: page === p })}
                      onClick={() => handleChangePage(null, Number(p))}
                    >
                      {p}
                    </button>
                  )
                );
              })()}
            </div>
            <button
              className={cx("pagination-button", "next")}
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page === totalPages}
            >
              <NextWhiteIcon className={cx("next-icon")} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BaseTableV2) as typeof BaseTableV2;
